import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './features/auth/AuthContext';
import { ProtectedRoute } from './features/auth/ProtectedRoute';
import { LoginPage } from './features/auth/LoginPage';
import { DashboardLayout } from './layouts/DashboardLayout';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { ServicesPage } from './features/services/ServicesPage';
import { EmployeesPage } from './features/employees/EmployeesPage';
import { ClientsPage } from './features/clients/ClientsPage';
import { ClientDetailPage } from './features/clients/ClientDetailPage';
import { SchedulesPage } from './features/schedules/SchedulesPage';
import { AppointmentsPage } from './features/appointments/AppointmentsPage';
import { OverduePage } from './features/appointments/OverduePage';
import { BookingWizard } from './features/publicBooking/BookingWizard';
import { CancelPage } from './features/publicBooking/CancelPage';
import { ReschedulePage } from './features/publicBooking/ReschedulePage';
import { NotificationsSettingsPage } from './features/notifications/NotificationsSettingsPage';
import { PrivacyPage } from './features/privacy/PrivacyPage';
import { ReportsPage } from './features/reports/ReportsPage';
import { CommissionRulesPage } from './features/commissions/CommissionRulesPage';
import { CommissionPayoutsPage } from './features/commissions/CommissionPayoutsPage';
import { PortfolioPage } from './features/gallery/PortfolioPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Publiczny portal (Faza 9) — bez auth, własny layout. */}
            <Route path="/rezerwacja" element={<BookingWizard />} />
            <Route path="/rezerwacja/anuluj/:token" element={<CancelPage />} />
            <Route path="/rezerwacja/przesun/:token" element={<ReschedulePage />} />
            {/* Polityka prywatności (Faza 11) — publiczna, bez auth. */}
            <Route path="/privacy" element={<PrivacyPage />} />

            <Route path="/login" element={<LoginPage />} />
            <Route
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route
                path="services"
                element={
                  <ProtectedRoute roles={['ADMIN']}>
                    <ServicesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="employees"
                element={
                  <ProtectedRoute roles={['ADMIN']}>
                    <EmployeesPage />
                  </ProtectedRoute>
                }
              />
              <Route path="clients" element={<ClientsPage />} />
              <Route path="clients/:id" element={<ClientDetailPage />} />
              <Route path="schedules" element={<SchedulesPage />} />
              <Route path="appointments" element={<AppointmentsPage />} />
              <Route path="appointments/overdue" element={<OverduePage />} />
              <Route
                path="settings/notifications"
                element={
                  <ProtectedRoute roles={['ADMIN']}>
                    <NotificationsSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="reports"
                element={
                  <ProtectedRoute roles={['ADMIN']}>
                    <ReportsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="commissions/rules"
                element={
                  <ProtectedRoute roles={['ADMIN']}>
                    <CommissionRulesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="commissions/payouts"
                element={
                  <ProtectedRoute roles={['ADMIN', 'EMPLOYEE']}>
                    <CommissionPayoutsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="portfolio"
                element={
                  <ProtectedRoute roles={['ADMIN', 'EMPLOYEE']}>
                    <PortfolioPage />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
